// src/services/ApiService.ts
/* eslint-disable max-len */
import axios, { AxiosResponse } from 'axios';
import { deleteFromStorage } from '@rehooks/local-storage';
import {
  Me, SignIn, StudentActivityDay, StudentEditResult, StudentFormData, StudentGroup,
  StudentGroupData, UserData, WordsListData,
} from './ApiService.types';

export const BASE_API_URL = process.env.REACT_APP_API_BASE_URL;

const getAxiosInstance = () => {
  const http = axios.create({
    baseURL: BASE_API_URL,
  });

  http.interceptors.request.use((config: any) => {
    const token = localStorage.getItem('token');
    if (token) {
      // eslint-disable-next-line no-param-reassign
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  });

  // Serwer uznał sesję za skończoną (konto usunięte, zmieniony e-mail) — jak przy wygasłym tokenie:
  // usunięcie tokenu przenosi na logowanie. 401 z samego logowania to złe dane, nie koniec sesji.
  http.interceptors.response.use(undefined, (error) => {
    const { config, response } = error;

    if (response?.status === 401 && config?.headers?.Authorization && config.url !== 'v1/sessions') {
      deleteFromStorage('token');
    }

    return Promise.reject(error);
  });

  return http;
};

// Types dla folderów
export interface FolderData {
  id: number;
  name: string;
  position: number;
  parentFolderId?: number;
  createdAt: Date;
  updatedAt: Date;
  metadata?: {
    totalListsCount: number;
    directListsCount: number;
    subfoldersCount: number;
  };
}

export interface FolderTreeItem {
  id: string;
  name: string;
  type: 'folder' | 'list';
  children?: FolderTreeItem[];
  parentId?: string | null;
  data?: {
    wordsCount?: number;
    shared?: boolean;
    updatedAt?: Date;
    createdAt?: Date | string;
    totalListsCount?: number;
    directListsCount?: number;
    subfoldersCount?: number;
  };
}

export interface FolderTreeResponse {
  items: FolderTreeItem[];
}

export interface TreeNodeType {
  wordsCount: number | undefined;
  words: any;
  updatedAt: any;
  combinedIds: never[];
  id: string;
  name: string;
  type: 'folder' | 'list';
  children?: TreeNodeType[];
  parentId?: string;
  data?: {
    createdAt: any;
    wordsCount?: number;
    shared?: boolean;
    updatedAt?: Date;
    totalListsCount?: number;
    directListsCount?: number;
    subfoldersCount?: number;
    sharedStudentIds?: number[];
  };
  sharedStudentIds?: number[];
}

export interface BreadcrumbItemType {
  id: string;
  name: string;
}

export interface FolderStatistics {
  totalFolders: number;
  totalLists: number;
  rootLists: number;
  sharedLists: number;
}

// Typy odpowiedzi proxy Unsplasha (R9). Klucz dostępu został po stronie backendu, więc front
// nie rozmawia już z api.unsplash.com — rozmawia z naszym API, z JWT jak wszędzie indziej.
export type UnsplashVariant = 'thumb' | 'small' | 'regular';
export type UnsplashOrientation = 'landscape' | 'portrait' | 'squarish';

export interface UnsplashSearchItem {
  photoId: string;
  description: string | null;
  width: number;
  height: number;
  color: string;
  blurHash: string | null;
  urls: {
    thumb: string;
    small: string;
    regular: string;
  };
  authorName: string;
  authorUrl: string;
  sourceUrl: string;
}

export interface UnsplashSearchResults {
  total: number;
  totalPages: number;
  page: number;
  perPage: number;
  results: UnsplashSearchItem[];
}

// Ten sam kształt, co `word.imageMeta` w zapisie listy — front kopiuje obiekt, nie składa go sam
export interface UnsplashImageMeta {
  source: 'unsplash';
  photoId: string;
  authorName: string;
  authorUrl: string;
  sourceUrl: string;
  normalizedUrl: string | null;
}

export interface UnsplashSelection {
  assetId: number;
  url: string;
  imageMeta: UnsplashImageMeta;
}

// Katalog wzorcowych obrazków. Kurator dodaje do niego pojedyncze pary zwrot → obrazek (tylko własne
// uploady), propozycje dostaje każdy nauczyciel. Błędy rozpoznajemy po `code`, nie po `message`.

// Podzbiór `word.imageMeta` z zapisu listy — wkładany do słowa bez zmian, bo nieznane pole daje 400
export interface CatalogImageMeta {
  source: string;
  photoId?: string;
  authorName?: string;
  authorUrl?: string;
  sourceUrl?: string;
  normalizedUrl: string | null;
}

export interface CatalogCandidate {
  assetId: number;
  url: string;
  imageMeta: CatalogImageMeta;
}

export interface CatalogCandidates {
  term: string;
  // `null` = pusty zwrot
  conceptKey: string | null;
  // Uporządkowane; [0] jest propozycją domyślną
  candidates: CatalogCandidate[];
}

// Obrazek dodany do katalogu przy innym, podobnym zwrocie. `conceptKey` to tamten zwrot, małymi literami,
// bo tak przechowuje go serwer — służy do podpisu w szufladzie; do słowa idzie tylko `imageMeta`.
export interface SimilarCatalogCandidate extends CatalogCandidate {
  conceptKey: string;
}

// Odpowiedź dla szuflady. `similar` dobiera i układa serwer, od najbliższych zwrotów, bez obrazków
// obecnych już w `candidates`. Masowe uzupełnianie (`lookup`) zostaje przy dokładnych trafieniach.
export interface CatalogSearchResult extends CatalogCandidates {
  similar: SimilarCatalogCandidate[];
}

// Szukanie po kawałkach słów w `GET /catalog`: `true` — kawałek w dowolnym miejscu słowa, jak na ekranie
// katalogu („rest" → „interested"); `'start'` — tylko całe słowa i ich początki („mia" → „miau")
export type CatalogFragment = true | 'start';

// Wpis żyje niezależnie od list: zmiana obrazka czy usunięcie słowa na liście go nie dotyka
export interface CatalogEntry {
  id: number;
  conceptKey: string;
  assetId: number;
  url: string | null;
  // Wycofanie jest miękkie — ponowne dodanie tej samej pary przywraca wpis
  active: boolean;
}

class ApiService {
  private axiosInstance = getAxiosInstance();

  public user = {
    signIn: async (email: string, password: string): Promise<SignIn> => (
      this.axiosInstance.post('v1/sessions', { email, password })
    ),
    signUp: async (data: any): Promise<any> => (
      this.axiosInstance.post('v1/users', data)
    ),
    activateAccount: async (token: string): Promise<void> => (
      this.axiosInstance.post('v1/users/activate', { token })
    ),
    activateStudentAccount: async (token: string, password: string): Promise<void> => (
      this.axiosInstance.post('v1/users/activate-student', { token, password })
    ),
    // Czy link ucznia jeszcze działa: te same zasady co aktywacja, ale niczego nie zużywa.
    // 200 — działa, 400 — nie. Token w ciele, nie w adresie, żeby nie trafiał do logów API.
    // Ten sam endpoint i `activateStudentAccount` obsługują też link do nowego hasła — rodzaj jest w tokenie
    checkStudentActivation: async (token: string): Promise<AxiosResponse<void>> => (
      this.axiosInstance.post('v1/users/activate-student/check', { token })
    ),
    me: async (): Promise<AxiosResponse<Me>> => (
      this.axiosInstance.get('v1/users/@me')
    ),
  };

  public photos = {
    add: async (
      data: FormData,
      // `publicUrl` to przestarzały alias `url` — backend trzyma go tylko dla starego bundla
    ): Promise<AxiosResponse<{ assetId?: number; url: string; publicUrl?: string }>> => (
      this.axiosInstance.post('v1/photos', data, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
    ),
  };

  public images = {
    // Wyszukiwanie w banku zdjęć. `orientation` nie jest ozdobnikiem — kafelki szuflady są poziome
    searchUnsplash: async (params: {
      query: string;
      page?: number;
      perPage?: number;
      orientation?: UnsplashOrientation;
    }): Promise<AxiosResponse<UnsplashSearchResults>> => (
      this.axiosInstance.get('v1/images/unsplash/search', { params })
    ),

    // Wybór zdjęcia: zakłada wiersz w `image_assets` i zgłasza użycie do Unsplasha.
    // Do backendu leci samo `photoId` — żaden adres z przeglądarki nie jest mu potrzebny.
    selectUnsplash: async (
      photoId: string,
      variant?: UnsplashVariant,
    ): Promise<AxiosResponse<UnsplashSelection>> => (
      this.axiosInstance.post('v1/images/unsplash/select', variant ? { photoId, variant } : { photoId })
    ),
  };

  public catalog = {
    // Zwrot idzie surowy — normalizuje backend i to jego wynik rozstrzyga, także o podobnych zwrotach.
    // `fragment` dokłada do `similar` zwroty, w których każde wpisane słowo jest kawałkiem słowa. Szuflada
    // wysyła `true` z pola wyszukiwania, a `'start'` dla zwrotu słowa, gdy bez niego katalog nic nie znalazł.
    // Bez niego odpowiedź jest taka jak dotąd.
    search: async (term: string, fragment?: CatalogFragment): Promise<AxiosResponse<CatalogSearchResult>> => (
      this.axiosInstance.get('v1/catalog', { params: fragment ? { term, fragment } : { term } })
    ),

    // `results[i]` odpowiada `terms[i]`; odpowiedź nie niesie niczego, po czym dałoby się dopasować słowo
    lookup: async (terms: string[]): Promise<AxiosResponse<{ results: CatalogCandidates[] }>> => (
      this.axiosInstance.post('v1/catalog/lookup', { terms })
    ),

    // Wpisy założone przez zalogowanego kuratora, łącznie z wycofanymi
    entries: async (): Promise<AxiosResponse<{ entries: CatalogEntry[] }>> => (
      this.axiosInstance.get('v1/catalog/entries')
    ),

    // 201 — nowy wpis, 200 — przywrócony (para była wycofana). Obrazek musi być uploadem tego
    // samego kuratora; cudzy dostaje tę samą odpowiedź co nieistniejący.
    add: async (term: string, assetId: number): Promise<AxiosResponse<CatalogEntry>> => (
      this.axiosInstance.post('v1/catalog/entries', { term, assetId })
    ),

    withdraw: async (entryId: number): Promise<AxiosResponse<void>> => (
      this.axiosInstance.delete(`v1/catalog/entries/${entryId}`)
    ),
  };

  public folders = {
    create: async (folderData: {
      name: string;
      parentFolderId?: number;
    }): Promise<AxiosResponse<FolderData>> => (
      this.axiosInstance.post('v1/folders', folderData)
    ),

    delete: async (id: number): Promise<AxiosResponse<null>> => (
      this.axiosInstance.delete(`v1/folders/${id}`)
    ),

    update: async (id: number, data: {
      name?: string;
      parentFolderId?: number | null;
    }): Promise<AxiosResponse<FolderData>> => (
      this.axiosInstance.put(`v1/folders/${id}`, data)
    ),

    rename: async (id: number, name: string): Promise<AxiosResponse<FolderData>> => (
      this.axiosInstance.patch(`v1/folders/${id}/rename`, { name })
    ),

    getTree: async (): Promise<AxiosResponse<FolderTreeResponse>> => (
      this.axiosInstance.get('v1/folders/tree')
    ),

    getTreeWithLists: async (): Promise<AxiosResponse<FolderTreeResponse>> => (
      this.axiosInstance.get('v1/folders/tree-with-lists')
    ),

    getBreadcrumbs: async (folderId: number): Promise<AxiosResponse<BreadcrumbItemType[]>> => (
      this.axiosInstance.get(`v1/folders/${folderId}/breadcrumbs`)
    ),

    getStatistics: async (): Promise<AxiosResponse<FolderStatistics>> => (
      this.axiosInstance.get('v1/folders/statistics')
    ),

    getChildren: async (folderId: number): Promise<AxiosResponse<{
      folders: FolderData[];
      lists: any[];
    }>> => (
      this.axiosInstance.get(`v1/folders/${folderId}/children`)
    ),

    move: async (data: {
      itemId: number;
      itemType: 'folder' | 'list';
      targetFolderId: number | null;
      position?: number;
    }): Promise<AxiosResponse<null>> => (
      this.axiosInstance.post('v1/folders/move', data)
    ),

    search: async (query: string): Promise<AxiosResponse<FolderTreeItem[]>> => (
      this.axiosInstance.get(`v1/folders/search?q=${encodeURIComponent(query)}`)
    ),

    bulkDelete: async (items: Array<{ id: number; type: string }>): Promise<AxiosResponse<{ deleted: number; errors: string[] }>> => (
      this.axiosInstance.post('v1/folders/bulk-delete', { items })
    ),
  };

  public wordsList = {
    create: async (wordsListData: {
      name: string;
      description?: string;
      folderId?: number;
    }): Promise<AxiosResponse<any>> => (
      this.axiosInstance.post('v1/words-lists', wordsListData)
    ),

    delete: async (id: number): Promise<AxiosResponse<null>> => (
      this.axiosInstance.delete(`v1/words-lists/${id}`)
    ),

    // Udostępnienia zmieniamy tylko dodawaniem i cofaniem, nie przełączaniem ani ustawianiem całego składu:
    // okno ze starym stanem zabrałoby wtedy listę uczniom, którzy dostali ją w międzyczasie (np. przez grupę).
    // Dodaje uczniowi listy, których jeszcze nie ma; posiadane pomija. Nieistniejąca albo cudza lista daje 404
    // i nic nie zostaje dodane
    addSharesToStudent: async (
      studentId: number,
      wordsListIds: number[],
    ): Promise<AxiosResponse<{ addedWordsListIds: number[] }>> => (
      this.axiosInstance.post(`v1/words-lists/share/student/${studentId}/add`, { wordsListIds })
    ),

    // Tylko cofa: lista nieudostępniona temu uczniowi jest pomijana. Całość w jednej transakcji — wszystko albo nic.
    revokeSharesFromStudent: async (
      studentId: number,
      wordsListIds: number[],
    ): Promise<AxiosResponse<{ revokedWordsListIds: number[] }>> => (
      this.axiosInstance.post(`v1/words-lists/share/student/${studentId}/revoke`, { wordsListIds })
    ),

    // Dopisuje listę członkom grupy, którzy jej nie mają; niczego nie zabiera. `sharedStudentIds` — wszyscy
    // uczniowie, którzy mają listę po zmianie
    shareWithGroup: async (
      listId: number,
      groupId: number,
    ): Promise<AxiosResponse<{ sharedStudentIds: number[]; addedStudentIds: number[] }>> => (
      this.axiosInstance.post(`v1/words-lists/${listId}/share/group/${groupId}`)
    ),

    // Zabiera listę wszystkim obecnym członkom grupy — także tym, którzy dostali ją osobno albo przez inną grupę
    revokeFromGroup: async (
      listId: number,
      groupId: number,
    ): Promise<AxiosResponse<{ sharedStudentIds: number[]; revokedStudentIds: number[] }>> => (
      this.axiosInstance.post(`v1/words-lists/${listId}/share/group/${groupId}/revoke`)
    ),

    getShares: async (
      listId: number,
    ): Promise<AxiosResponse<{
      wordsListId: number;
      sharedStudents: any[];
      sharedStudentIds: number[];
    }>> => (
      this.axiosInstance.get(`v1/words-lists/${listId}/shares`)
    ),

    duplicate: async (id: number): Promise<AxiosResponse<any>> => (
      this.axiosInstance.post(`v1/words-lists/${id}/duplicate`)
    ),

    edit: async (id: number, userData: UserData): Promise<AxiosResponse<null>> => (
      this.axiosInstance.put(`v1/words-lists/${id}`, userData)
    ),

    moveToFolder: async (listId: number, folderId: number | null): Promise<AxiosResponse<any>> => (
      this.axiosInstance.patch(`v1/words-lists/${listId}/move`, { folderId })
    ),

    getRootLists: async (): Promise<AxiosResponse<any[]>> => (
      this.axiosInstance.get('v1/words-lists/root')
    ),

    getByFolder: async (folderId: number): Promise<AxiosResponse<any[]>> => (
      this.axiosInstance.get(`v1/words-lists/folder/${folderId}`)
    ),

    list: async (page: number = 1): Promise<AxiosResponse<{
      records: {
        name: string;
        id: number;
        folderId?: number;
        folder?: FolderData;
      }[]
    }>> => (
      this.axiosInstance.get(`v1/words-lists?page=${page}`)
    ),

    getOne: async (id: string): Promise<AxiosResponse<{
      name: string;
      id: number;
      combinedId: any[];
      words: any[];
      folderId?: number;
      sharedStudentIds?: number[];
    }>> => (
      this.axiosInstance.get(`v1/words-lists/${id}`)
    ),

    // Zapis listy nie zmienia udostępnień — serwer pomija `sharedStudentIds`, więc go nie wysyłamy
    save: async (
      id: string,
      values: {
        name: string;
        description: string;
        words: any[];
        folderId?: number | null;
      },
    ): Promise<AxiosResponse<{
      name: string;
      description: string;
      id: number;
      words: any[];
      sharedStudentIds?: number[];
      folderId?: number;
    }>> => {
      const payload: Record<string, any> = {
        name: values.name,
        description: values.description,
        words: values.words,
      };

      if (values.folderId !== undefined) {
        payload.folderId = values.folderId;
      }

      return this.axiosInstance.put(`v1/words-lists/${id}`, payload);
    },
  };

  public students = {
    create: async (studentData: StudentFormData): Promise<AxiosResponse<UserData>> => (
      this.axiosInstance.post('v1/students', studentData)
    ),
    delete: async (id: number): Promise<AxiosResponse<null>> => (
      this.axiosInstance.delete(`v1/students/${id}`)
    ),
    edit: async (id: number, studentData: StudentFormData): Promise<AxiosResponse<StudentEditResult>> => (
      this.axiosInstance.put(`v1/students/${id}`, studentData)
    ),
    // Nowy link unieważnia poprzedni. Ten sam uczeń dostanie kolejny najwcześniej po 2 minutach (429)
    sendActivationLink: async (id: number): Promise<AxiosResponse<UserData>> => (
      this.axiosInstance.post(`v1/students/${id}/activation-link`)
    ),
    // Tylko dla ucznia, który aktywował konto. Link ważny 24 godziny; obecne hasło działa, dopóki
    // uczeń nie ustawi nowego. Limit 2 minut jak przy linku aktywacyjnym
    sendPasswordLink: async (id: number): Promise<AxiosResponse<UserData>> => (
      this.axiosInstance.post(`v1/students/${id}/password-link`)
    ),
    list: async (page: number = 1): Promise<AxiosResponse<{ records: UserData[] }>> => (
      this.axiosInstance.get(`v1/students?page=${page}`)
    ),
    // Wszystkie listy udostępnione uczniowi, każda z pełnymi słowami — bez podziału na strony
    wordsList: async (page: number = 1): Promise<AxiosResponse<{ records: WordsListData[] }>> => (
      this.axiosInstance.get(`v1/students/@me/words-lists?page=${page}`)
    ),
    // Monitoring aktywności, okno „Aktywność”: dni z ćwiczeniami od `from` do `to` (YYYY-MM-DD, czas polski, obie
    // daty wliczone), każdy z listami. Próby ucznia wysyła src/services/activity.ts
    activityDays: async (id: number, from: string, to: string): Promise<AxiosResponse<{ days: StudentActivityDay[] }>> => (
      this.axiosInstance.get(`v1/students/${id}/activity?from=${from}&to=${to}`)
    ),
  };

  // Grupy uczniów nauczyciela. Usunięcie grupy ani wypisanie z niej nikomu list nie zabiera; usunięty uczeń
  // sam znika ze swoich grup. Nazwa: 1–100 znaków po obcięciu spacji, unikalna bez względu na wielkość liter
  // (409 `GROUP_NAME_TAKEN`). Grupa cudza albo usunięta — 404 `GROUP_NOT_FOUND`
  public studentGroups = {
    // Wszystkie grupy naraz, w dowolnej kolejności
    list: async (): Promise<AxiosResponse<{ records: StudentGroup[] }>> => (
      this.axiosInstance.get('v1/student-groups')
    ),
    create: async (data: StudentGroupData): Promise<AxiosResponse<StudentGroup>> => (
      this.axiosInstance.post('v1/student-groups', data)
    ),
    // Pominięte pole zostaje bez zmian
    update: async (id: number, data: Partial<StudentGroupData>): Promise<AxiosResponse<StudentGroup>> => (
      this.axiosInstance.put(`v1/student-groups/${id}`, data)
    ),
    delete: async (id: number): Promise<AxiosResponse<void>> => (
      this.axiosInstance.delete(`v1/student-groups/${id}`)
    ),
  };

  public contact = {
    create: async (payload: any): Promise<AxiosResponse<void>> => (
      this.axiosInstance.post('/v1/contact', payload)
    ),
  };
}

export default new ApiService();
